import type { DeepMutable, Unfurl } from '@shencom/typing';
import { cloneDeep } from 'lodash-es';
import { ToArray } from '../../array/src';

type Dictionary<T = any> = Record<string, T>;

interface TreeNodeOptions {
  children?: string;
  id?: string;
  pid?: string;
}

interface TreeToArrayOptions {
  children?: string;
  keepChildren?: boolean;
}
type Flatten<T> = T extends any[] ? T[number] : T;
type AnyToArray<T> = T extends any[] ? T : T[];
type TreeType = Dictionary[] | Dictionary;

/**
 * 树形数据平铺开转数组
 *
 * @template T
 * @param {T} tree - 数据
 * @param {TreeToArrayOptions} [options] - 配置属性
 * @param {string} [options.children='children'] - 子节点映射字段
 * @param {boolean} [options.keepChildren=false] - 是否保留完整子节点数据
 * @example
 * ```
 * TreeToArray(tree);
 * TreeToArray(tree, { children: 'child'});
 * TreeToArray(tree, { children: 'child', keepChildren: true });
 * ```
 * @return {*}  {AnyToArray<T>}
 */
export function TreeToArray<T>(tree: T, options?: TreeToArrayOptions): AnyToArray<T> {
  const { keepChildren = false, children = 'children' } = options || {};

  const data = ToArray(tree);

  const flattenedData = <AnyToArray<T>>[];

  function traverse(nodes: T[]) {
    nodes.forEach((node: any) => {
      const item = node ? { ...node } : node;
      if (node) {
        if (!keepChildren) delete item[children];

        if (node[children]) {
          traverse(node[children]);
        }
      }
      flattenedData.push(item);
    });
  }

  traverse(data);

  return flattenedData;
}

/**
 * 查找树形数据中的某个节点
 *
 * @template T
 * @param {T} tree - 数据
 * @param {string} id - 节点 id
 * @param {Omit<TreeNodeOptions, 'pid'>} [maps] - 配置属性
 * @param {string} [maps.id='id'] - 节点 id 映射字段
 * @param {string} [maps.children='children'] - 子节点映射字段
 * @example
 * ```
 * TreeFindNode(tree, '1');
 * TreeFindNode(tree, '1', { id: 'id1', children: 'child'});
 * ```
 * @return {*}  {(Flatten<T> | null)}
 */
export function TreeFindNode<T>(
  tree: T,
  id: string,
  maps?: Omit<TreeNodeOptions, 'pid'>,
): Flatten<T> | null {
  const map_id = maps?.id || 'id';
  const map_children = maps?.children || 'children';

  function traverse(nodes: T[]): Flatten<T> | null {
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i] as any;
      if (!node) break;
      if (node[map_id] === id) {
        return node as Flatten<T>;
      }

      if (node[map_children]?.length) {
        const result = traverse(node[map_children]);
        if (result) return result;
      }
    }
    return null;
  }

  const res = traverse(ToArray(tree));

  return res;
}

/**
 * 查找树形数据中的某个节点的所有父节点，返回数据中包含自身
 *
 * @template T
 * @param {TreeType} tree - 数据
 * @param {string} id - 节点 id
 * @param {TreeNodeOptions} [maps] - 配置属性
 * @param {string} [maps.id='id'] - 节点 id 映射字段
 * @param {string} [maps.children='children'] - 子节点映射字段
 * @param {string} [maps.pid='pid'] - 父节点 id 映射字段
 * @param {boolean} [keepChildren=false] - 是否保留完整子节点数据
 * @example
 * ```
 * TreeFindParentNodes(tree, '1');
 * TreeFindParentNodes(tree, '1', { id: 'id', children: 'child', pid: 'pid'});
 * TreeFindParentNodes(tree, '1', { id: 'id', children: 'child', pid: 'pid' }, true);
 * ```
 * @return {*}  {T[]}
 */
export function TreeFindParentNodes<T>(
  tree: T,
  id: string,
  maps?: TreeNodeOptions,
  keepChildren = false,
): AnyToArray<T> {
  const mapKey = {
    children: maps?.children || 'children',
    id: maps?.id || 'id',
    pid: maps?.pid || 'pid',
  };

  const result = <AnyToArray<T>>[];

  const process = TreeToArray(tree, { children: mapKey.children, keepChildren });

  function findParent(_id: string) {
    const node = process.find((item) => item[mapKey.id] === _id);
    if (node) {
      result.unshift(node);
      if (node[mapKey.pid]) findParent(node[mapKey.pid]);
    }
  }
  findParent(id);

  return result;
}

/**
 * 查找树形数据中的某个节点的所有父节点id
 *
 * @param {TreeType} tree - 数据
 * @param {string} id - 节点 id
 * @param {TreeNodeOptions} [maps] - 配置属性
 * @param {string} [maps.id='id'] - 节点 id 映射字段
 * @param {string} [maps.children='children'] - 子节点映射字段
 * @param {string} [maps.pid='pid'] - 父节点 id 映射字段
 * @example
 * ```
 * TreeFindParentIds(tree, '1');
 * TreeFindParentIds(tree, '1', { id: 'id', children: 'child', pid: 'pid'});
 * ```
 * @return {*}  {string[]}
 */
export function TreeFindParentIds(tree: TreeType, id: string, maps?: TreeNodeOptions): string[] {
  const mapKey = {
    children: maps?.children || 'children',
    id: maps?.id || 'id',
    pid: maps?.pid || 'pid',
  };

  const nodes = TreeFindParentNodes(tree, id, mapKey);

  return nodes.map((item) => item[mapKey.id]);
}

interface ParentByKeyProps extends TreeNodeOptions {
  key: string;
  value: number | string;
}

/**
 * 通过树形数据中的某个 key 的值，查找所有的父级节点，返回数据中包含自身
 *
 * @template T
 * @param {T} tree - 数据
 * @param {ParentByKeyProps} [props] - 配置属性
 * @param {string} [props.key] - 要查找的 key
 * @param {string | number} [props.value] - 要查找的 key 的值
 * @param {string} [props.id='id'] - 节点 id 映射字段
 * @param {string} [props.pid='pid'] - 父节点 id 映射字段
 * @param {string} [props.children='children'] - 子节点映射字段
 * @param {boolean} [keepChildren='false'] - 是否保留完整子节点数据
 * @example
 * ```
 * TreeFindParentByKey(tree, { key: 'name', value: '1' });
 * TreeFindParentByKey(tree, { key: 'name', value: '1', id: 'id', pid: 'pid', children: 'child' });
 * TreeFindParentByKey(tree, { key: 'name', value: '1', id: 'id', pid: 'pid', children: 'child' }, true);
 * ```
 * @return {*} {T[]}
 */
export function TreeFindParentByKey<T>(
  tree: T,
  props: ParentByKeyProps,
  keepChildren?: boolean,
): AnyToArray<T> {
  const { key, value, id = 'id', pid = 'pid', children = 'children' } = props;

  const nodes = TreeToArray(tree, {
    children,
    keepChildren,
  });
  const node = nodes.find((item) => item[key] === value);

  if (!node) return [] as AnyToArray<T>;

  const result = <AnyToArray<T>>[node];

  let parent_id = node?.[pid] as string;

  while (parent_id !== undefined) {
    const parent = nodes.find((item) => item[id] === parent_id);
    parent_id = parent?.[pid];
    if (parent) {
      if (!keepChildren) delete parent[children];
      result.unshift(parent);
    }
  }

  return result;
}

interface TreeCallbackfn<T> {
  (node: T): (T & {}) | Record<string, any>;
}

/**
 * 处理树形结构每一项的数据
 *
 * @template T
 * @param {T} tree - 数据
 * @param {TreeCallbackfn<Flatten<T>>} handler - 处理函数
 * @param {Pick<TreeNodeOptions, 'children'>} [maps] - 配置属性
 * @param {string} [maps.children='children'] - 子节点映射字段
 * @example
 * ```
 * TreeMap(tree, (node) => ({ ...node, title: node.name }));
 * TreeMap(tree, (node) => ({ ...node, title: node.name }), { children: 'child'});
 * ```
 * @return {*}  {AnyToArray<T>}
 */
export function TreeMap<T>(
  tree: T,
  handler: TreeCallbackfn<Flatten<T>>,
  maps?: Pick<TreeNodeOptions, 'children'>,
) {
  const { children = 'children' } = maps || {};
  tree = cloneDeep(tree);

  function processTree(nodes: AnyToArray<T>) {
    const modifiedArray: T[] = [];
    for (let i = 0; i < nodes.length; i++) {
      let item = nodes[i];

      if (item && item[children]?.length) {
        item[children] = processTree(item[children]);
      }

      item = handler(cloneDeep(item) as Flatten<T>) as T;

      modifiedArray.push(item);
    }
    return modifiedArray;
  }

  const nodes = processTree(ToArray(tree) as AnyToArray<T>);

  if (Array.isArray(tree)) {
    return nodes as T;
  }

  return nodes[0] as T;
}

/**
 * 遍历树形结构每一项的数据
 *
 * @template T
 * @param {T} tree - 数据
 * @param {(node: T) => void} handler - 处理函数
 * @param {Pick<TreeNodeOptions, 'children'>} [maps] - 配置属性
 * @param {string} [maps.children='children'] - 子节点映射字段
 * @example
 * ```
 * TreeForEach(tree, (node) => { ... });
 * TreeForEach(tree, (node) => { ... }, { children: 'child'});
 * ```
 */
export function TreeForEach<T>(
  tree: T,
  handler: (node: T) => void,
  maps?: Pick<TreeNodeOptions, 'children'>,
) {
  TreeMap(
    tree,
    (node: any) => {
      handler(cloneDeep(node));
      return node;
    },
    maps,
  );
}

type Pop<T extends any[]> = T extends [...infer P, any] ? P : [];

type MapToValue<MM extends Dictionary, TT extends any[], O = []> = TT['length'] extends 0
  ? O
  : MapToValue<
      MM,
      Pop<TT>,
      {
        [K in keyof MM as MM[K]]: TT[number][K] extends any[]
          ? MapToValue<MM, TT[number][K], TT[number][K][0]> extends never
            ? never
            : [MapToValue<MM, TT[number][K], TT[number][K][0]>]
          : TT[number][K];
      }
    >;

/**
 * 对树形结构中每一项的进行映射
 *
 * @template T
 * @param {T} tree - 数据
 * @param {Record<string, string>} maps - 映射关系
 * @param {string} [childKey='children'] - 子节点字段
 * @example
 * ```
 * TreeMapOption(tree, { title: 'name' });
 * TreeMapOption(tree, { title: 'name' }, 'child');
 * ```
 * @return {*} {AnyToArray<T>}
 */
export const TreeMapOption = <
  T extends TreeType,
  M extends Dictionary<keyof Flatten<T> | (string & {})>,
  C extends string,
>(
  tree: T,
  maps: M,
  childKey?: C,
) => {
  const nodes = TreeMap(
    tree,
    (node) => {
      const item = node;
      if (maps && Object.keys(maps).length) {
        Object.entries(maps).forEach(([k, v]) => {
          item[v] = item[k];
          delete item[k];
        });
      }
      return item;
    },
    { children: childKey || 'children' },
  );

  type TItem = AnyToArray<T>;

  type Res = MapToValue<M, TItem>;

  return nodes as Unfurl<DeepMutable<Res>>;
};

/**
 * 过滤树形结构中的每一项的数据
 *
 * @template T
 * @param {T} tree - 数据
 * @param {(node: T) => void} condition - 判断函数
 * @param {Pick<TreeNodeOptions, 'children'>} [maps] - 配置属性
 * @param {string} [maps.children='children'] - 子节点映射字段
 * @example
 * ```
 * TreeFilter(tree, (node) => node.active);
 * TreeFilter(tree, (node) => node.active, { children: 'child'});
 * ```
 * @return {*}  {T}
 */
export function TreeFilter<T>(
  tree: T,
  condition: (node: Flatten<T>) => boolean,
  maps?: Pick<TreeNodeOptions, 'children'>,
): T | null {
  if (!tree) return null;

  const nodes = cloneDeep(Array.isArray(tree) ? tree[0] : tree) as Dictionary;

  function handler(node: Dictionary) {
    const { children = 'children' } = maps || {};

    const filteredChildren: T[] = [];
    if ((node[children] as any)?.length) {
      for (const child of node[children]) {
        const filteredChild = handler(child) as T;
        if (filteredChild) {
          filteredChildren.push(filteredChild);
        }
      }
    }

    const newRoot = { ...node, [children]: filteredChildren };

    if (condition(newRoot as Flatten<T>) || newRoot[children]?.length) {
      return newRoot as Flatten<T>;
    }
    return null;
  }

  const res = handler(nodes);

  if (Array.isArray(tree)) {
    return (res ? [res] : null) as T;
  }

  return res;
}

/**
 * 将子节点列表为空数组处理成 null
 *
 * @template T
 * @param {T} tree - 数据
 * @param {Pick<TreeNodeOptions, 'children'>} [maps] - 配置属性
 * @param {string} [maps.children='children'] - 子节点映射字段
 * @example
 * ```
 * TreeFilterChildEmpty(tree);
 * TreeFilterChildEmpty(tree, { children: 'child'});
 * ```
 * @return {*}  {T}
 */
export function TreeFilterChildEmpty<T>(tree: T, maps?: Pick<TreeNodeOptions, 'children'>): T {
  const { children = 'children' } = maps || {};

  const nodes = ToArray(cloneDeep(tree));

  function deleteEmptyChildren(arr: Dictionary[]) {
    arr?.forEach((node) => {
      if (Object.prototype.hasOwnProperty.call(node, children)) {
        if (node[children]?.length > 0) deleteEmptyChildren(node[children]);
        else delete node[children];
      }
    });
  }

  deleteEmptyChildren(nodes as Dictionary[]);

  if (Array.isArray(tree)) {
    return nodes as T;
  }

  return nodes[0] as T;
}
